import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Field } from "./shared";
import { ledgerGroupLabels } from "./constants";

const ROOT_TYPE_LABELS = { ASSET: "Assets", LIABILITY: "Liabilities", FUND: "Funds / Capital", INCOME: "Income", EXPENSE: "Expenses" };
const ROOT_TYPE_ORDER = ["ASSET", "LIABILITY", "FUND", "INCOME", "EXPENSE"];

export default function LedgerForm({ form, setForm, accountGroups, groupLocked, busy, onSubmit, onCancel, submitLabel, isEdit }) {
  const groupsByRoot = ROOT_TYPE_ORDER.map((rootType) => ({
    rootType,
    groups: accountGroups.filter((g) => g.rootType === rootType && g.active !== false),
  })).filter((entry) => entry.groups.length);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Ledger name">
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Lift maintenance vendor" />
        </Field>
        <Field label="Ledger code (optional)">
          <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="LDG-001" />
        </Field>
        <Field label="Ledger type">
          <Select value={form.ledgerGroup} onValueChange={(v) => setForm((f) => ({ ...f, ledgerGroup: v }))} disabled={groupLocked}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ledgerGroupLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Account group (Chart of Accounts)">
          <Select value={form.accountGroupId || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, accountGroupId: v === "__none__" ? "" : v }))} disabled={groupLocked}>
            <SelectTrigger><SelectValue placeholder="No group" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No group</SelectItem>
              {groupsByRoot.map(({ rootType, groups }) => (
                <SelectGroup key={rootType}>
                  <SelectLabel>{ROOT_TYPE_LABELS[rootType]}</SelectLabel>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          {groupLocked && <p className="mt-1 text-xs text-amber-600">Posted vouchers already reference this ledger - its type/account group can no longer be changed. Create a new ledger instead if the classification is wrong.</p>}
        </Field>
        <Field label="Status">
          <label className="mt-2 flex items-center gap-2 text-sm text-[#374151]">
            <Checkbox checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: !!v }))} />
            Active
          </label>
        </Field>
      </div>

      <Field label="Notes">
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
      </Field>

      <div className="rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Opening Balance</p>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Amount">
            <Input
              type="number" min="0" step="0.01" disabled={groupLocked}
              value={form.openingBalance} onChange={(e) => setForm((f) => ({ ...f, openingBalance: e.target.value }))}
              placeholder="0.00"
            />
          </Field>
          <Field label="Balance is">
            {form.ledgerGroup === "cash" ? (
              <Input value="Dr (cash can only open in debit)" disabled className="bg-[#F3F4F6] text-[#6B7280]" />
            ) : (
              <Select value={form.openingSide} onValueChange={(v) => setForm((f) => ({ ...f, openingSide: v }))} disabled={groupLocked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">Dr</SelectItem>
                  <SelectItem value="credit">Cr</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="As of date">
            <Input type="date" disabled={groupLocked} value={form.openingAsOfDate || ""} onChange={(e) => setForm((f) => ({ ...f, openingAsOfDate: e.target.value }))} />
          </Field>
        </div>
        <p className="mt-2 text-xs text-[#6B7280]">
          {groupLocked
            ? "This ledger already has posted transactions, so its opening balance can no longer be changed here. Post a dated Journal Entry instead to make a correction."
            : "What this ledger already held as of the date above. Saved immediately with the ledger - no separate posting step."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button disabled={busy || !form.name.trim()} onClick={onSubmit} className="bg-[#111827] text-white hover:bg-[#374151]">
          {submitLabel}
        </Button>
        {onCancel && <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>}
      </div>
    </div>
  );
}
